import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines clsx and tailwind-merge to create a powerful className utility.
 * 
 * Features:
 * - Conditionally apply classes using clsx syntax
 * - Automatically resolve Tailwind CSS class conflicts using tailwind-merge
 * - Support for all clsx input types (strings, objects, arrays, etc.)
 * 
 * @param {...any} inputs - Class names, objects, arrays, or any clsx-compatible input
 * @returns {string} - Merged and deduplicated class string
 * 
 * @example
 * // Basic usage
 * cn('px-2 py-1', 'bg-blue-500') // "px-2 py-1 bg-blue-500"
 * 
 * // Conditional classes
 * cn('px-2 py-1', isActive && 'bg-blue-500') // "px-2 py-1 bg-blue-500" (if isActive is true)
 * 
 * // Object syntax
 * cn('px-2 py-1', { 'bg-blue-500': isActive, 'bg-gray-500': !isActive })
 * 
 * // Tailwind conflict resolution
 * cn('px-2 px-4') // "px-4" (px-4 overrides px-2)
 * cn('bg-red-500', 'bg-blue-500') // "bg-blue-500" (last one wins)
 * 
 * // Complex example with arrays and conditions
 * cn([
 *   'px-2 py-1',
 *   isLoading && 'opacity-50 cursor-not-allowed',
 *   variant === 'primary' && 'bg-blue-500 text-white',
 *   variant === 'secondary' && 'bg-gray-500 text-white',
 *   size === 'sm' && 'text-sm',
 *   size === 'lg' && 'text-lg'
 * ])
 */
export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default cn;